/**
 * Tracking V2 - Visitor- & Session-IDs, vollständige Attribution.
 *
 * Verhalten:
 *  - visitor_id: persistent über alle Besuche (localStorage)
 *  - session_id: pro Browser-Tab/Session (sessionStorage)
 *  - First-Touch UTMs werden NIE überschrieben
 *  - Last-Touch UTMs werden bei jedem Besuch aktualisiert (wenn vorhanden)
 *  - gclid / wbraid / gbraid / fbclid / msclkid + gad_*-Parameter werden gespeichert
 *  - Device / Browser / OS-Detection clientseitig
 *
 * Wird komplementär zu trackingClient.js betrieben — bestehende Events bleiben unangetastet.
 */

const VISITOR_KEY = "bekaj_visitor";
const SESSION_KEY = "bekaj_session";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const CLICK_ID_KEYS = ["gclid", "wbraid", "gbraid", "fbclid", "msclkid"];
const GAD_KEYS = [
  "gad_source", "gad_campaignid", "gad_adgroupid", "gad_keyword",
  "gad_creative", "gad_matchtype", "gad_device",
];

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "v-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
};

const safeParse = (raw) => { try { return JSON.parse(raw); } catch { return null; } };

const readParams = (keys) => {
  const out = {};
  try {
    const sp = new URLSearchParams(window.location.search);
    keys.forEach((k) => {
      const v = sp.get(k);
      if (v) out[k] = v;
    });
  } catch { /* noop */ }
  return out;
};

const detectDeviceType = () => {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet|playbook|kindle/.test(ua)) return "tablet";
  if (/mobi|iphone|ipod|android|blackberry|opera mini|iemobile/.test(ua)) return "mobile";
  return "desktop";
};

const detectBrowser = () => {
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Andere";
};

const detectOS = () => {
  const ua = navigator.userAgent || "";
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac OS X/.test(ua) && !/iPhone|iPad/.test(ua)) return "macOS";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Linux/.test(ua)) return "Linux";
  return "Andere";
};

/** Heuristische Bot-Erkennung client-seitig.
 *  Headless/Crawler-Signale: Webdriver, fehlende Sprachen, bekannte Bot-UA. */
const detectIsBot = () => {
  try {
    const ua = (navigator.userAgent || "").toLowerCase();
    if (/bot|spider|crawler|slurp|bingpreview|googlebot|yandex|baidu|duckduckbot|facebookexternalhit|whatsapp|telegrambot|headlesschrome|phantomjs|puppeteer|playwright/.test(ua)) return true;
    if (navigator.webdriver === true) return true;
    if (!navigator.languages || navigator.languages.length === 0) return true;
    return false;
  } catch { return false; }
};

/** Initialisiert/aktualisiert Visitor- und Session-Records. Idempotent. */
export const initVisitorSession = () => {
  // Visitor (persistent)
  let visitor = safeParse(localStorage.getItem(VISITOR_KEY)) || null;
  const nowIso = new Date().toISOString();

  const urlUtm = readParams(UTM_KEYS);
  const urlClickIds = readParams(CLICK_ID_KEYS);
  const urlGad = readParams(GAD_KEYS);

  if (!visitor) {
    visitor = {
      visitor_id: uuid(),
      first_visit: nowIso,
      initial_referrer: document.referrer || "",
      initial_landing_page: window.location.pathname + window.location.search,
      // First-Touch UTM (locked)
      initial_utm_source: urlUtm.utm_source || null,
      initial_utm_medium: urlUtm.utm_medium || null,
      initial_utm_campaign: urlUtm.utm_campaign || null,
      initial_utm_term: urlUtm.utm_term || null,
      initial_utm_content: urlUtm.utm_content || null,
      // First click-IDs (only set initially)
      initial_gclid: urlClickIds.gclid || null,
      initial_fbclid: urlClickIds.fbclid || null,
      page_views: 0,
    };
  }

  // Last-Touch UTMs aktualisieren (nur wenn URL aktuelle UTMs liefert)
  if (Object.keys(urlUtm).length > 0) {
    UTM_KEYS.forEach((k) => { if (urlUtm[k]) visitor[k] = urlUtm[k]; });
  }
  // Click-IDs (immer aktualisieren, wenn neu)
  CLICK_ID_KEYS.forEach((k) => { if (urlClickIds[k]) visitor[k] = urlClickIds[k]; });
  GAD_KEYS.forEach((k) => { if (urlGad[k]) visitor[k] = urlGad[k]; });

  visitor.last_visit = nowIso;
  visitor.page_views = (visitor.page_views || 0) + 1;

  localStorage.setItem(VISITOR_KEY, JSON.stringify(visitor));

  // Session (per tab)
  let session = safeParse(sessionStorage.getItem(SESSION_KEY)) || null;
  if (!session) {
    session = {
      session_id: uuid(),
      session_start: nowIso,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  return { visitor, session };
};

/** Aggregiert die komplette Attribution für API-Calls (Lead-Create etc.). */
export const getAttributionPayload = () => {
  const { visitor, session } = initVisitorSession();
  return {
    visitor_id: visitor.visitor_id,
    session_id: session.session_id,
    // Last-Touch UTMs
    utm_source: visitor.utm_source || null,
    utm_medium: visitor.utm_medium || null,
    utm_campaign: visitor.utm_campaign || null,
    utm_term: visitor.utm_term || null,
    utm_content: visitor.utm_content || null,
    // First-Touch UTMs
    initial_utm_source: visitor.initial_utm_source || null,
    initial_utm_medium: visitor.initial_utm_medium || null,
    initial_utm_campaign: visitor.initial_utm_campaign || null,
    initial_utm_term: visitor.initial_utm_term || null,
    initial_utm_content: visitor.initial_utm_content || null,
    // Click-IDs
    gclid: visitor.gclid || visitor.initial_gclid || null,
    wbraid: visitor.wbraid || null,
    gbraid: visitor.gbraid || null,
    fbclid: visitor.fbclid || visitor.initial_fbclid || null,
    msclkid: visitor.msclkid || null,
    // gad_*
    gad_source: visitor.gad_source || null,
    gad_campaignid: visitor.gad_campaignid || null,
    gad_adgroupid: visitor.gad_adgroupid || null,
    gad_keyword: visitor.gad_keyword || null,
    gad_creative: visitor.gad_creative || null,
    gad_matchtype: visitor.gad_matchtype || null,
    gad_device: visitor.gad_device || null,
    // Context
    initial_referrer: visitor.initial_referrer || "",
    initial_landing_page: visitor.initial_landing_page || "",
    referrer: document.referrer || "",
    landing_page: window.location.pathname + window.location.search,
    first_visit_timestamp: visitor.first_visit || null,
    first_visit: visitor.first_visit || null,
    last_visit: visitor.last_visit || null,
    // Device
    device_type: detectDeviceType(),
    browser: detectBrowser(),
    os: detectOS(),
    screen_size: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    language: navigator.language || "",
    page_view_count: visitor.page_views || 1,
    is_bot: detectIsBot(),
  };
};

/** Sendet ein page_view-Event an /api/tracking/event (mit voller Attribution). */
let __lastPath = null;
let __sessionStartMs = null;
let __pageLoadMs = null;
let __firstClickMs = null;
let __lastActionName = null;
let __maxScrollPct = 0;
let __scrollThresholdsFired = new Set();

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Generic event sender. ALLE Tracking-Events (Click/Scroll/Page/Exit) laufen hier durch.
 * Versendet via sendBeacon wenn möglich (fire-and-forget, übersteht Navigation).
 *
 * @param {string} eventName       z.B. "page_view" | "click" | "scroll_depth" | "page_exit"
 * @param {object} extra           freie Properties — landet in `extra`-Feld (zukunftssicher)
 */
export const trackEvent = (eventName, extra = {}) => {
  try {
    const attrib = getAttributionPayload();
    const body = JSON.stringify({
      event_name: eventName,
      event_type: extra.event_type || (eventName === "page_view" ? "view" : "click"),
      page_url: window.location.href,
      ...attrib,
      extra: { ...extra, time_on_page_ms: performance.now() - (__pageLoadMs || performance.now()) },
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${BACKEND}/api/tracking/event`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${BACKEND}/api/tracking/event`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true,
      }).catch(() => {});
    }
    __lastActionName = eventName;
  } catch { /* swallow */ }
};

export const trackPageView = async () => {
  const path = window.location.pathname + window.location.search;
  if (path === __lastPath) return; // dedup bei React-Re-Renders
  __lastPath = path;
  __pageLoadMs = performance.now();
  __firstClickMs = null;
  __maxScrollPct = 0;
  __scrollThresholdsFired = new Set();
  if (!__sessionStartMs) __sessionStartMs = Date.now();
  trackEvent("page_view", { event_type: "view" });
};

// =========================================================
// UNIVERSAL CLICK / SCROLL / TIME / EXIT TRACKING
// =========================================================

/**
 * Initialisiert globale Listener für:
 *  - [data-track] Click-Delegation (1 Listener für ALLE Buttons)
 *  - Scroll-Depth (25/50/75/100, einmal pro Pageload)
 *  - Page-Exit (visibilitychange + pagehide) mit scroll_depth, time_on_page, last_action
 *
 * Idempotent: bei mehrfachem Aufruf wird nur 1x installiert.
 */
let __universalInstalled = false;
export const installUniversalTracking = () => {
  if (__universalInstalled || typeof window === "undefined") return;
  __universalInstalled = true;
  __pageLoadMs = performance.now();

  // --- 1) Click-Delegation auf data-track ---
  document.addEventListener("click", (e) => {
    const el = e.target?.closest?.("[data-track]");
    if (!el) return;
    const name = el.getAttribute("data-track") || "click";
    const value = el.getAttribute("data-track-value") || undefined;
    const href = el.getAttribute("href") || undefined;
    // First-Click-Latenz festhalten
    if (__firstClickMs === null) {
      __firstClickMs = performance.now();
    }
    trackEvent(name, {
      event_type: "click",
      label: el.getAttribute("aria-label") || el.textContent?.trim()?.slice(0, 60) || "",
      href,
      value,
      time_to_first_click_ms: __firstClickMs ? Math.round(__firstClickMs - __pageLoadMs) : null,
    });
  }, { capture: true, passive: true });

  // --- 2) Scroll-Tracking ---
  const onScroll = () => {
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    if (docH <= 0) return;
    const pct = Math.min(100, Math.max(0, Math.round((window.scrollY / docH) * 100)));
    if (pct > __maxScrollPct) __maxScrollPct = pct;
    for (const t of [25, 50, 75, 100]) {
      if (pct >= t && !__scrollThresholdsFired.has(t)) {
        __scrollThresholdsFired.add(t);
        trackEvent("scroll_depth", { event_type: "view", scroll_pct: t });
      }
    }
  };
  let scrollT = null;
  window.addEventListener("scroll", () => {
    if (scrollT) return;
    scrollT = setTimeout(() => { scrollT = null; onScroll(); }, 200);
  }, { passive: true });

  // --- 3) Exit-Tracking (visibilitychange + pagehide) ---
  let __exitSent = false;
  const sendExit = () => {
    if (__exitSent) return;
    __exitSent = true;
    trackEvent("page_exit", {
      event_type: "exit",
      scroll_depth_pct: __maxScrollPct,
      last_action: __lastActionName,
      time_on_page_ms: Math.round(performance.now() - (__pageLoadMs || performance.now())),
    });
  };
  // pagehide ist robuster als beforeunload (mobile safari)
  window.addEventListener("pagehide", sendExit, { capture: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendExit();
  });
};
